import { axiosTokenInstance } from 'src/utils/axiosInstance.utils';
import {
  apiUrls,
  companySettings,
  profileSettings,
} from 'src/utils/properties.utils';

export interface CompanyInfoData {
  companyName: string;
  country: string;
  logoUrl: string | null;
  addressLine1: string;
  addressLine2: string;
  city: string;
  postCode: string;
  telephoneNumber: string;
  email: string;
  websiteUrl: string;
}

interface CompanyInfo {
  companyName: string;
  companyRegNo: string;
  country: string;
  addressLine1: string;
  addressLine2: string;
  postalCode: string;
  city: string;
  telephoneNumber: string;
  websiteUrl: string;
  logo: string;
  email: string;
}

interface UserProfileInfo {
  firstName: string;
  lastName: string;
  countryCode: string;
  phoneNumber: string;
  avatar: string;
}

export const fetchCompanyInfo = async (): Promise<
  CompanyInfo & { logoUrl: string }
> => {
  const response = await axiosTokenInstance.get(apiUrls.GET_COMPANY_BASIC_INFO);

  const fetchedData: CompanyInfo = {
    companyName: response.data.name || '',
    companyRegNo: response.data.registrationNo || '',
    country: response.data.country || '',
    logo:
      response.data.logo ||
      `${companySettings.LOGO_PATH_PREFIX}${companySettings.DEFAULT_LOGO}`,
    addressLine1: response.data.addressLine1 || '',
    addressLine2: response.data.addressLine2 || '',
    postalCode: response.data.postalCode || '',
    city: response.data.city || '',
    telephoneNumber: response.data.telephoneNumber || '',
    websiteUrl: response.data.websiteUrl || '',
    email: response.data.email || '',
  };

  const logoUrl = formatAssetUrl(fetchedData.logo);

  return {
    ...fetchedData,
    logoUrl,
  };
};

export const getCompanyInfoForPDF = async (): Promise<CompanyInfoData> => {
  try {
    const info = await fetchCompanyInfo();
    return {
      companyName: info.companyName,
      country: info.country,
      logoUrl: info.logoUrl || null,
      addressLine1: info.addressLine1,
      addressLine2: info.addressLine2,
      city: info.city,
      postCode: info.postalCode,
      telephoneNumber: info.telephoneNumber,
      email: info.email,
      websiteUrl: info.websiteUrl,
    };
  } catch (error) {
    console.error('Error fetching company info for PDF:', error);
    // Return empty but valid object as fallback
    return {
      companyName: 'OrderX',
      country: '',
      logoUrl: null,
      addressLine1: '',
      addressLine2: '',
      city: '',
      postCode: '',
      telephoneNumber: '',
      email: '',
      websiteUrl: '',
    };
  }
};

export const fetchUserProfileInfo = async (): Promise<
  UserProfileInfo & { avatarUrl: string }
> => {
  const response = await axiosTokenInstance.get(apiUrls.GET_PROFILE_INFO);

  const fetchedData: UserProfileInfo = {
    firstName: response.data.firstName,
    lastName: response.data.lastName,
    countryCode:
      response.data.countryCode || profileSettings.DEFAULT_COUNTRY_CODE,
    phoneNumber: response.data.phoneNumber || '',
    avatar:
      response.data.avatar ||
      `${profileSettings.AVATAR_PATH_PREFIX}${profileSettings.DEFAULT_AVATAR}`,
  };

  const avatarUrl = formatAssetUrl(fetchedData.avatar);

  return {
    ...fetchedData,
    avatarUrl,
  };
};

export const formatAssetUrl = (assetPath: string): string => {
  return `${process.env.REACT_APP_API_BASE_URL}${assetPath}`;
};

export const calculateBoxHeight = (errorCount: number): number => {
  return (
    companySettings.BASE_BOX_HEIGHT +
    errorCount * companySettings.ERROR_HEIGHT_INCREMENT
  );
};
